import { useState } from 'react';
import { AxiosResponse } from 'axios';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { subMonths } from 'date-fns/fp';
import { AmplitudeEvent, logAmplitudeEvent } from '@algoan/dashboard/analytics';
import { AsyncStatus } from '@algoan/dashboard/model';
import { messages, downloadMessages } from './application-messages';
import { FoldersCsvConfig } from './application-model';
import { getCsvRequestArguments, getFileName, getCustomersCsvUrl } from './application-utils';
import {
  applicationTableConfigSelector,
  teamConfigSelector,
  accessTokenTeamSelector,
  projectIdSelector,
  widgetConfigSelector,
} from '@algoan/dashboard/store/selectors';
import { ExportByDates } from '../dashboard/export-dates';
import { CsvIcon } from '../../shared-components/icons';
import { setToEndOfDay, setToFirstDayOfMonth } from '@algoan/dashboard/utils';
import { useAsyncDownload } from '../../hooks/use-async-download';
import { DownloadDropdown } from '../../shared-components/atomic-components';
import { useDispatch } from '@algoan/dashboard/store/store';
import { authRequest } from '@algoan/dashboard/store/thunks';
import { useModal } from '../../shared-components/global-modal/use-modal';

export const DownloadMenu: React.FC = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const tableConfig = useSelector(applicationTableConfigSelector);
  const team = useSelector(accessTokenTeamSelector);
  const teamConfigInformation = useSelector(teamConfigSelector);
  const { Modal } = useModal();
  const { downloadStatus, runDownload } = useAsyncDownload();
  const projectId = useSelector(projectIdSelector);
  const widgetConfig = useSelector(widgetConfigSelector);

  const isExporting = downloadStatus === AsyncStatus.PENDING;
  const date = new Date();
  const endDate = setToEndOfDay(date);
  const startDate = subMonths(1)(setToFirstDayOfMonth(date));
  const invitationEnabled = widgetConfig?.invitation;

  const getCsvConfig = (): FoldersCsvConfig => {
    const columnsFromConfig =
      tableConfig?.additionalColumns?.map((column) => ({
        path: column.path,
        name: column.label,
      })) ?? [];

    const teamColumn =
      !team && teamConfigInformation
        ? {
            path: 'dashboard.team',
            name: intl.formatMessage(messages.teamColumn),
          }
        : [];

    const applicationCreatorColumn = invitationEnabled
      ? {
          path: 'dashboard.invitationHistory.0.senderUsername',
          name: intl.formatMessage(messages.applicationCreatorColumn),
        }
      : [];

    return {
      columns: columnsFromConfig
        .concat(teamColumn)
        .concat(applicationCreatorColumn)
        .map((column) => ({ ...column, path: column.path })),
    };
  };

  const [csvDownloadModalOpen, setCsvDownloadModalOpen] = useState(false);
  const handleDownloadModalOpen = (): void => setCsvDownloadModalOpen(true);
  const handleDownloadModalClose = (): void => setCsvDownloadModalOpen(false);

  const handleExportAll = (): void => {
    const url = getCustomersCsvUrl({ projectId });
    const fileName = getFileName();
    const { method, config } = getCsvRequestArguments(getCsvConfig());
    const axiosRequest = (): Promise<AxiosResponse<string>> =>
      dispatch(authRequest<string>(method, url, undefined, { ...config, responseType: 'blob' }));

    logAmplitudeEvent(AmplitudeEvent.NAVIGATION_DOWNLOAD_ALL);
    runDownload({ axiosRequest, fileName, type: 'text/csv' });
  };

  const handleExportByDates = async (startDate: Date | null, endDate: Date | null): Promise<void> => {
    const isDateValid = (startDate !== null && startDate) <= (endDate !== null && endDate);
    if (!isDateValid) return Promise.reject();

    const url = getCustomersCsvUrl({ startDate, endDate, projectId });
    const fileName = getFileName();
    const { method, config } = getCsvRequestArguments(getCsvConfig());
    const axiosRequest = (): Promise<AxiosResponse<string>> =>
      dispatch(authRequest<string>(method, url, undefined, { ...config, responseType: 'blob' }));

    logAmplitudeEvent(AmplitudeEvent.NAVIGATION_DOWNLOAD, { startDate, endDate });
    runDownload({ axiosRequest, fileName, type: 'text/csv', onSuccess: handleDownloadModalClose });
  };

  const optionsList = [
    {
      display: intl.formatMessage(downloadMessages['exportDates']),
      onDownload: handleDownloadModalOpen,
      isLoading: false,
      icon: <CsvIcon />,
    },
    {
      display: intl.formatMessage(downloadMessages['exportAll']),
      onDownload: handleExportAll,
      isLoading: isExporting,
      icon: <CsvIcon />,
    },
  ];

  return (
    <>
      <Modal open={csvDownloadModalOpen} onClose={handleDownloadModalClose} id="csv-download-modal">
        <ExportByDates
          initialStartDate={startDate}
          initialEndDate={endDate}
          loading={isExporting}
          onDownload={handleExportByDates}
        />
      </Modal>
      <DownloadDropdown options={optionsList} />
    </>
  );
};
